import type { UserStateEntry, UserStateItemKey, UserStateMap } from "../../types/user-state";
import { isUserStateItemKey } from "../../types/user-state";
import type { UpdateUserStateMutationArgs } from "./userStateOptimisticQuery";

/**
 * Ports `redux/services/userStateApi.ts`'s `syncLocalUserStateToRemote`: when a user signs in
 * and the server has no user-state yet (a brand-new account, or an existing one that has never
 * synced anything) but the client already holds local entries — e.g. jobs saved while anonymous,
 * earlier in this page session — push those entries up to the server (version 0, so the PUT
 * creates rather than conflicts) instead of silently losing them. Framework-agnostic like the
 * rest of this module: `updateItem` is whatever PUT the caller wires in (see
 * `useUserStateQuery.ts`, which passes the raw `putUserStateItem` API call — not the optimistic
 * mutation hook, since this bulk resync doesn't need per-item optimistic UI).
 */
export async function syncLocalUserStateToRemote(
  localEntries: UserStateMap,
  updateItem: (args: UpdateUserStateMutationArgs) => Promise<UserStateEntry>
): Promise<UserStateMap> {
  const entriesToSync = Object.entries(localEntries).filter(([itemKey, entry]) => {
    if (!isUserStateItemKey(itemKey)) return false;
    return Boolean(entry.data && Object.keys(entry.data).length > 0);
  });

  const syncedEntries: UserStateMap = {};

  await Promise.all(
    entriesToSync.map(async ([itemKey, entry]) => {
      try {
        const result = await updateItem({
          itemKey: itemKey as UserStateItemKey,
          data: entry.data,
          version: 0,
        });
        syncedEntries[itemKey] = result;
      } catch {
        // Keep the local entry if the sync PUT fails — better to retain it client-side than
        // silently drop it, matching the original's behavior.
        syncedEntries[itemKey] = entry;
      }
    })
  );

  return syncedEntries;
}
